import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { CloseIcon, HStack, IconButton, ScrollView, Spacer, useMediaQuery, VStack } from 'native-base';
import { keys, orderBy } from 'lodash';
import { useTranslation } from 'react-i18next';

import { Button, ButtonGroup, Text } from '@pimm/base';
import { AddUpdateSiteGoalRequest, DaypartProfileInput, DaysOfTheWeekEnum } from '@pimm/services/lib/sms-drivethru/types';
import { AddUpdateSiteGoal } from '@pimm/services/lib/sms-drivethru/services';
import { useSiteConfig } from '@app/features/store-core';
import { FormDataSiteGoals, useFormSiteGoals } from '../hooks';
import { useSiteDailyGoals } from '../state';
import { SettingsStoreGoals } from './settings-store-goals';
import { SettingsStoreGuidelines } from './settings-store-guidelines';

type GoalSettingsProps = {
  onClose?: () => void;
};

export const GoalSettings = (props: GoalSettingsProps) => {
  const { t } = useTranslation();
  const [isSmallScreen] = useMediaQuery({ maxWidth: 1200 });
  const queryClient = useQueryClient();
  const { siteConfig } = useSiteConfig();

  const [tabFocus, setTabFocus] = useState<'guidelines' | 'goals'>('goals');

  const goals = useSiteDailyGoals(state => state.goals);
  const form = useFormSiteGoals({
    defaultValues: {
      weekdays: orderBy(siteConfig.config?.storeHoursConfig?.dayparts, ['partNumber'], ['asc']).map(_ => ({
        label: _.label,
        partNumber: _.partNumber,
        dpStartTime: _.startTime,
        goalProfileId: 0,
        goalProfileName: 'Custom',
      })),
    },
  });

  const addUpdateSiteGoals = useMutation({
    mutationFn: async (formData: FormDataSiteGoals & { siteId?: string }) => {
      const { isSevenDays, siteId, weekdays, weekends } = formData;
      const fieldInputs = {
        [DaysOfTheWeekEnum.Weekdays]: weekdays,
        [DaysOfTheWeekEnum.Weekends]: weekends,
      };

      const addUpdateRequest: AddUpdateSiteGoalRequest[] = keys(fieldInputs)
        .map(key => {
          const inputs: DaypartProfileInput[] = [];
          const fields = fieldInputs[key];
          let daysOfWeek = key as DaysOfTheWeekEnum;

          if (isSevenDays && daysOfWeek === DaysOfTheWeekEnum.Weekends) return undefined;

          fields.forEach(_field => {
            const input: DaypartProfileInput = {
              dpStartTime: _field.dpStartTime,
              goalProfileId: _field.goalProfileId,
              goalProfileName: _field.goalProfileName,
            };
            // Form custom goal values
            if (!input.goalProfileId) {
              input.greet = _field.greet;
              input.menuBoard = _field.menuBoard;
              input.pickup = _field.pickup;
              input.total = _field.total;
            }
            inputs.push(input);
          });

          // Replace daysOfWeek into All if the user didn't select weekdays/weekend
          if (isSevenDays && daysOfWeek === DaysOfTheWeekEnum.Weekdays) {
            daysOfWeek = DaysOfTheWeekEnum.All;
          }

          return {
            daysOfWeek: daysOfWeek,
            daypartProfiles: inputs,
            siteId: siteId,
          };
        })
        .filter(payload => !!payload);
      const promise = Promise.all(addUpdateRequest.map(payload => AddUpdateSiteGoal(payload)));
      await promise;
    },
    onSuccess: () => {
      const queryCache = queryClient.getQueryCache();
      queryCache.getAll().forEach(cache => {
        const isValidQuery = ['GetWeeklyData', 'GetSiteGoals', 'GetTimerData'].some(key => cache.queryKey.includes(key));

        if (isValidQuery) {
          queryClient.invalidateQueries({ queryKey: [cache.queryKey[0]] });
          queryClient.refetchQueries({ queryKey: cache.queryKey });
        }
      });
      if (props.onClose) props.onClose();
    },
  });

  const handleFormSave = () => {
    if (goals) {
      addUpdateSiteGoals.mutate({ ...form.getValues(), siteId: siteConfig.id });
    }
  };

  return (
    <VStack h="full">
      <HStack space={3} alignItems="center" pl={4} pr={3} py={2} minH="56px" borderBottomWidth={1}>
        <Text size="xl" fontWeight={700} color="gray.900">
          {t('sos:goal_settings')}
        </Text>

        <Spacer />

        {isSmallScreen && (
          <ButtonGroup value={tabFocus} onChange={setTabFocus}>
            <ButtonGroup.Item value="guidelines">{t('sos:goal_guidelines')}</ButtonGroup.Item>
            <ButtonGroup.Item value="goals">{t('sos:store_goals')}</ButtonGroup.Item>
          </ButtonGroup>
        )}

        {!!props.onClose && (
          <IconButton
            p={2}
            _pressed={{ bg: 'gray.100' }}
            _hover={{ bg: 'gray.100' }}
            icon={<CloseIcon size="14px" color="gray.700" />}
            onPress={props.onClose}
          />
        )}
      </HStack>

      <HStack flex={1}>
        {(!isSmallScreen || tabFocus === 'guidelines') && (
          <ScrollView
            w={{ base: 'full', xl: '1/2' }}
            contentContainerStyle={{ flexGrow: 1 }}
            borderRightWidth={{ xl: 1 }}
            borderColor="gray.200"
          >
            <SettingsStoreGuidelines />
          </ScrollView>
        )}

        {(!isSmallScreen || tabFocus === 'goals') && (
          <ScrollView w={{ base: 'full', xl: '1/2' }} contentContainerStyle={{ flexGrow: 1 }}>
            <SettingsStoreGoals form={form} />
          </ScrollView>
        )}
      </HStack>

      <HStack alignItems="center" px={4} h="56px" borderTopWidth={1} justifyContent="flex-end" space={3}>
        <Button variant="unstyled" minWidth={90} disabled={addUpdateSiteGoals.isLoading} onPress={props.onClose}>
          {t('sos:cancel')}
        </Button>
        <Button
          minWidth={100}
          isLoading={addUpdateSiteGoals.isLoading}
          disabled={!form.formState.isDirty}
          onPress={form.handleSubmit(handleFormSave)}
        >
          {t('sos:save')}
        </Button>
      </HStack>
    </VStack>
  );
};
